"use client";

/**
 * Opens Paddle's payment form inside Rankbist's own checkout window (components/CheckoutModal.tsx, mounted once in
 * the root layout) instead of Paddle's full-screen overlay. Any page can call openCheckoutModal; the window shows our
 * order summary (filled from Paddle's own totals, tax included) next to Paddle's embedded card / PayPal form.
 */
export type CheckoutRequest = {
  transactionId: string;
  /** What is being bought, shown until Paddle reports the exact item (e.g. "Advanced", "100 URL checks"). */
  title: string;
  /** "plan" goes to the welcome page when paid; "credits" back to Plan & billing, where the credits are confirmed. */
  kind: "plan" | "credits";
  /** False hides Paddle's "Add discount" field (an offer this buyer can't use). Shown by default. */
  allowDiscountCodes?: boolean;
};

type Listener = (req: CheckoutRequest | null) => void;
const listeners = new Set<Listener>();
let current: CheckoutRequest | null = null;

export function openCheckoutModal(req: CheckoutRequest) {
  current = req;
  listeners.forEach((fn) => fn(current));
}

export function closeCheckoutModal() {
  current = null;
  listeners.forEach((fn) => fn(null));
}

export function onCheckoutModal(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

/** Where the buyer goes once Paddle says the payment is complete. */
export function afterPaid(req: CheckoutRequest): string {
  const txn = encodeURIComponent(req.transactionId);
  return req.kind === "plan" ? `/dashboard/welcome?txn=${txn}` : `/dashboard/billing?credits_txn=${txn}`;
}
